import type React from 'react';

type TextField = HTMLInputElement | HTMLTextAreaElement;

const NON_TEXT_INPUT_TYPES = ['checkbox', 'radio', 'submit', 'button', 'file', 'hidden'];

// Zero width skips hidden inputs, like the one Select renders for autofill.
const isTextField = (el: Element): el is TextField => {
  return (
    (el instanceof HTMLTextAreaElement ||
      (el instanceof HTMLInputElement && !NON_TEXT_INPUT_TYPES.includes(el.type))) &&
    !el.disabled &&
    !el.readOnly &&
    el.offsetWidth > 0
  );
};

// Selects keep their value in a hidden input or native select; empty means a choice is still to make.
const isUnchosenSelect = (el: Element) => {
  return (
    (el instanceof HTMLSelectElement ||
      (el instanceof HTMLInputElement && !NON_TEXT_INPUT_TYPES.includes(el.type) && el.offsetWidth === 0)) &&
    !el.disabled &&
    !el.value
  );
};

const isFull = (field: TextField) => {
  return !!field.placeholder && field.value.length >= field.placeholder.length;
};

// Invalid fields and half-typed dates or phone numbers still need typing, even with a value in them.
const needsInput = (field: TextField) => {
  return (
    !field.value ||
    field.getAttribute('data-has-error') === 'true' ||
    (field.hasAttribute('data-auto-advance') && !isFull(field))
  );
};

// Skips what autofill or an address suggestion already filled, so Return lands where typing is still needed.
const getNextTextField = (field: HTMLInputElement) => {
  const fields = Array.from(field.form?.elements ?? []).filter(isTextField);
  const index = fields.indexOf(field);
  if (index === -1) return undefined;
  const after = fields.slice(index + 1);
  return after.find(needsInput) ?? after[0];
};

// Moving on by itself must not hop over a select still waiting for a choice, like Nationality.
const getFieldAfterFullDate = (field: HTMLInputElement) => {
  const controls = Array.from(field.form?.elements ?? []);
  for (const control of controls.slice(controls.indexOf(field) + 1)) {
    if (isTextField(control) && needsInput(control)) return control;
    if (isUnchosenSelect(control)) return undefined;
  }
  return undefined;
};

// Desktop keeps submit-on-Enter from any field, the behavior keyboard users expect.
// Hosts without matchMedia, like jsdom in the idv package's tests, count as desktop.
const isTouchScreen = () => {
  return window.matchMedia?.('(pointer: coarse)')?.matches ?? false;
};

const AUTOFILL_SETTLE_MS = 250;

// The mask types over digits, so a date stays full while it's edited; only the keystroke that fills it moves on.
const fullDates = new WeakSet<HTMLInputElement>();

const rememberFullness = (field: HTMLInputElement) => {
  if (isFull(field)) fullDates.add(field);
  else fullDates.delete(field);
};

// On a phone the browser submits on Return from any field, which flags every empty one; only the last field submits.
const focusNextFieldOnEnter = (event: React.KeyboardEvent) => {
  const field = event.target;
  const { defaultPrevented, isComposing, keyCode } = event.nativeEvent;
  // keyCode 229 is the Enter that confirms an IME composition in Safari, where isComposing is already false.
  if (event.key !== 'Enter' || defaultPrevented || isComposing || keyCode === 229) return;
  if (!(field instanceof HTMLInputElement) || !isTouchScreen()) return;
  const next = getNextTextField(field);
  if (!next) return;
  event.preventDefault();
  next.focus();
};

// Safari AutoFill can scroll the form far down while it types, then focus a field it left above the screen.
const revealIfScrolledPast = (field: HTMLInputElement) => {
  const visibleTop = window.visualViewport?.offsetTop ?? 0;
  if (field.getBoundingClientRect().bottom < visibleTop) field.scrollIntoView({ block: 'center' });
};

const prepareField = (event: React.FocusEvent) => {
  const field = event.target;
  if (!(field instanceof HTMLInputElement) || !field.form || !isTouchScreen()) return;
  field.setAttribute('enterkeyhint', getNextTextField(field) ? 'next' : 'done');
  if (field.hasAttribute('data-auto-advance')) rememberFullness(field);
  revealIfScrolledPast(field);
};

// Number pads have no Return key, so a date or phone field moves on once its mask is full.
const advanceWhenFull = (event: React.FormEvent) => {
  const field = event.target;
  if (!(field instanceof HTMLInputElement) || !field.hasAttribute('data-auto-advance') || !isTouchScreen()) return;
  // Safari AutoFill types like a person but moves focus on by itself; waiting lets it go first.
  setTimeout(() => {
    const wasFull = fullDates.has(field);
    rememberFullness(field);
    if (wasFull || !isFull(field) || document.activeElement !== field) return;
    const next = getFieldAfterFullDate(field);
    if (next) next.focus();
    else field.blur();
  }, AUTOFILL_SETTLE_MS);
};

export const fieldNavigationHandlers = {
  onKeyDown: focusNextFieldOnEnter,
  onFocus: prepareField,
  onInput: advanceWhenFull,
};
